import { useEffect, useState } from 'react';

interface DisplayedValue<T> {
  value: T;
  /** When this value started showing (ms since epoch). */
  since: number;
}

/**
 * Keep each displayed value on screen for at least `minDisplayMs` before
 * showing the next one — for live labels that would otherwise flicker when the
 * source changes several times in quick succession.
 *
 * Not a debounce. A trailing debounce restarts its timer on every change, so a
 * steady burst of changes would freeze the label on a stale value for the
 * whole burst. Here the first value shows immediately; a change that arrives
 * while the current value is still inside its minimum window waits out the
 * remainder, and then the LATEST value shows (values that came and went in
 * between are skipped). A change after the window shows on the next tick.
 *
 * @example
 * ```typescript
 * // A step title that completes in 200ms never flashes; the label advances at
 * // most every 1.5s, always to the newest title.
 * const shownTitle = useMinimumDisplayTime(latestStepTitle, 1500);
 * ```
 */
export function useMinimumDisplayTime<T>(value: T, minDisplayMs: number): T {
  const [displayed, setDisplayed] = useState<DisplayedValue<T>>(() => ({
    value,
    since: Date.now(),
  }));

  useEffect(() => {
    if (Object.is(value, displayed.value)) {
      return;
    }
    const remainingMs = Math.max(
      0,
      minDisplayMs - (Date.now() - displayed.since)
    );
    const timer = setTimeout(() => {
      setDisplayed({ value, since: Date.now() });
    }, remainingMs);
    return () => clearTimeout(timer);
    // `displayed` is read for its current value at effect-fire time (to
    // compute the remaining window and bail out via Object.is), not as a
    // reactive trigger — including it re-runs this effect every time the
    // timer itself sets `displayed`, which the early-return makes harmless
    // but is pure noise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, minDisplayMs]);

  return displayed.value;
}
